import { getFunctionUrl } from "../config";
import { getAuthToken } from "./auth";
import { getSupabaseClient } from "./supabase";

const REQUEST_TIMEOUT_MS = 15_000;

export type DwbRunStatus =
  | "draft"
  | "selected"
  | "reviewed"
  | "ready"
  | "submitting"
  | "submitted"
  | "failed"
  | string;

export type DwbReviewStatus = "pending" | "approved" | "rejected" | string;

export interface DwbIssue extends Record<string, unknown> {
  code?: string;
  message?: string;
  date?: string;
  employee_name?: string;
  source_text?: string;
}

export interface DwbSourceEmployee extends Record<string, unknown> {
  project_employee_id?: string;
}

export interface DwbSourceDay extends Record<string, unknown> {
  date?: string;
  day_report_id?: string;
  revision?: number;
  updated_at?: string;
  employees?: DwbSourceEmployee[];
}

export interface DwbWeekSource extends Record<string, unknown> {
  days?: DwbSourceDay[];
}

export interface DwbAdapterDay extends Record<string, unknown> {
  activities?: string[];
}

export interface DwbWeekAdapter extends Record<string, unknown> {
  ready?: boolean;
  blockers?: DwbIssue[];
  warnings?: DwbIssue[];
  draft_week_input?: {
    days?: DwbAdapterDay[];
    [key: string]: unknown;
  };
}

export interface DwbCard extends Record<string, unknown> {
  dwb_card_id: string;
  employee_name?: string;
  selection_order?: number;
  selected_date?: string;
  review_status?: DwbReviewStatus;
  review_note?: string | null;
  reviewed_at?: string | null;
  observation_type?: string;
  category?: string;
  activity_type?: string;
  observation_text?: string;
  entry_id?: string;
}

export interface DwbWeekRunSummary extends Record<string, unknown> {
  dwb_week_run_id?: string;
  iso_week?: string;
  status?: DwbRunStatus;
}

export interface DwbWeekRun extends Record<string, unknown> {
  dwb_week_run_id: string;
  iso_week: string;
  status: DwbRunStatus;
  revision: number;
  selection_revision?: number;
  adapter_ready?: boolean;
  selector_version?: string | null;
  selector_commit?: string | null;
  selection_seed?: string | null;
  source_snapshot?: DwbWeekSource | null;
  adapter_snapshot?: DwbWeekAdapter | null;
  cards?: DwbCard[];
}

export interface DwbSelectionCardInput extends Record<string, unknown> {
  week: string;
  project_employee_id: string;
  naam: string;
  datum: string;
  entry_id: string;
  activity_type: string;
  type_observatie: string;
  categorie: string;
  omschrijving: string;
}

export interface DwbDryRunAttempt extends Record<string, unknown> {
  request_payload?: {
    fields?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

export interface DwbOperationResult extends Record<string, unknown> {
  status?: string;
  code?: string;
  run?: DwbWeekRun;
  attempts?: DwbDryRunAttempt[];
}

async function rpc<T>(
  name: string,
  args: Record<string, unknown>,
  fallbackMessage: string,
): Promise<T> {
  const { data, error } = await getSupabaseClient().rpc(name, args);
  if (error) throw new Error(error.message || fallbackMessage);
  return data as T;
}

async function fetchJsonWithAuth<T>(url: string): Promise<T> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("Je sessie ontbreekt of is verlopen. Meld opnieuw aan.");
  }

  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      signal: controller.signal,
    });
    const payload: unknown = await response.json().catch(() => ({}));
    if (!response.ok) {
      const record = payload && typeof payload === "object" && !Array.isArray(payload)
        ? payload as Record<string, unknown>
        : {};
      if (response.status === 401) {
        throw new Error("Je sessie is verlopen. Meld opnieuw aan.");
      }
      if (response.status === 403) {
        throw new Error("Je hebt geen toegang tot deze DWB-gegevens.");
      }
      const message = typeof record.message === "string"
        ? record.message
        : typeof record.error === "string"
        ? record.error
        : `DWB-servicefout (${response.status}).`;
      throw new Error(message);
    }
    return payload as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("De DWB-service reageert niet binnen 15 seconden.");
    }
    if (error instanceof Error && !error.message.startsWith("Failed to fetch")) {
      throw error;
    }
    throw new Error("Netwerkfout bij het laden van de DWB-week.");
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

export const dwbApi = {
  getWeekSource: (projectId: string, isoWeek: string) =>
    rpc<DwbWeekSource>(
      "get_dwb_week_source",
      { target_project_id: projectId, target_iso_week: isoWeek },
      "DWB-weekbron laden mislukt",
    ),

  getWeekAdapter: (projectId: string, isoWeek: string) => {
    const params = new URLSearchParams({ project_id: projectId, week: isoWeek });
    return fetchJsonWithAuth<DwbWeekAdapter>(
      getFunctionUrl("dbvdw-dwb", `?${params.toString()}`),
    );
  },

  listWeekRuns: (projectId: string, limit = 100) =>
    rpc<DwbWeekRunSummary[]>(
      "list_dwb_week_runs",
      { target_project_id: projectId, result_limit: limit },
      "DWB-weekruns laden mislukt",
    ),

  getWeekRun: (runId: string) =>
    rpc<DwbWeekRun>(
      "get_dwb_week_run",
      { target_dwb_week_run_id: runId },
      "DWB-weekrun laden mislukt",
    ),

  createWeekRun: (
    projectId: string,
    isoWeek: string,
    sourceSnapshot: DwbWeekSource,
    adapterSnapshot: DwbWeekAdapter,
    idempotencyKey: string,
  ) =>
    rpc<DwbOperationResult>(
      "create_dwb_week_run",
      {
        target_project_id: projectId,
        target_iso_week: isoWeek,
        source_snapshot: sourceSnapshot,
        adapter_snapshot: adapterSnapshot,
        idempotency_key: idempotencyKey,
      },
      "DWB-weekrun aanmaken mislukt",
    ),

  refreshWeekRun: (
    runId: string,
    expectedRevision: number,
    sourceSnapshot: DwbWeekSource,
    adapterSnapshot: DwbWeekAdapter,
    idempotencyKey: string,
  ) =>
    rpc<DwbOperationResult>(
      "refresh_dwb_week_run",
      {
        target_dwb_week_run_id: runId,
        expected_revision: expectedRevision,
        source_snapshot: sourceSnapshot,
        adapter_snapshot: adapterSnapshot,
        idempotency_key: idempotencyKey,
      },
      "DWB-weekrun verversen mislukt",
    ),

  setWeekSelection: (
    runId: string,
    expectedRevision: number,
    selectorVersion: string,
    selectorCommit: string,
    selectionSeed: string,
    cards: DwbSelectionCardInput[],
    idempotencyKey: string,
  ) =>
    rpc<DwbOperationResult>(
      "set_dwb_week_selection",
      {
        target_dwb_week_run_id: runId,
        expected_revision: expectedRevision,
        idempotency_key: idempotencyKey,
        selector_version: selectorVersion,
        selector_commit: selectorCommit,
        selection_seed: selectionSeed,
        cards,
      },
      "DWB-selectie importeren mislukt",
    ),

  reviewCard: (
    cardId: string,
    expectedRevision: number,
    decision: "approved" | "rejected",
    reviewNote: string | null,
    idempotencyKey: string,
  ) =>
    rpc<DwbOperationResult>(
      "review_dwb_card",
      {
        target_dwb_card_id: cardId,
        expected_revision: expectedRevision,
        idempotency_key: idempotencyKey,
        decision,
        review_note: reviewNote,
      },
      "DWB-kaart beoordelen mislukt",
    ),

  resetWeekSelection: (
    runId: string,
    expectedRevision: number,
    reason: string,
    idempotencyKey: string,
  ) =>
    rpc<DwbOperationResult>(
      "reset_dwb_week_selection",
      {
        target_dwb_week_run_id: runId,
        expected_revision: expectedRevision,
        idempotency_key: idempotencyKey,
        reason,
      },
      "DWB-selectie resetten mislukt",
    ),

  markWeekReady: (
    runId: string,
    expectedRevision: number,
    idempotencyKey: string,
  ) =>
    rpc<DwbOperationResult>(
      "mark_dwb_week_ready",
      {
        target_dwb_week_run_id: runId,
        expected_revision: expectedRevision,
        idempotency_key: idempotencyKey,
      },
      "DWB-week gereedmaken mislukt",
    ),

  prepareFormsDryRun: (
    runId: string,
    expectedRevision: number,
    idempotencyKey: string,
  ) =>
    rpc<DwbOperationResult>(
      "prepare_dwb_forms_dry_run",
      {
        target_dwb_week_run_id: runId,
        expected_revision: expectedRevision,
        idempotency_key: idempotencyKey,
      },
      "Microsoft Forms dry-run voorbereiden mislukt",
    ),
};
